/**
 * @file PoolBurnReport.tsx
 * @brief Company report — Enterprise seat + shared minute pool burn.
 *
 * @details
 * Composes GET /api/company/enterprise (no dedicated report endpoint).
 * Period comes from the live Enterprise plan, not the Analytics range bar.
 *
 * @references
 * - features/employees/api.ts (useCompanyEnterpriseStatus)
 * - docs/product/subscriptions.md
 *
 * @author DES
 * @date 27-07-2026
 */

import { useState } from "react";
import { Gauge, Users } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import {
  GridBand,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { useCompanyEnterpriseStatus } from "@/features/employees/api";
import { formatDate } from "@/shared/utils/formatters";
import { defaultReportRange } from "../defaultRange";
import { ReportShell } from "./ReportShell";

function displayName(row: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
}): string {
  const name = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  return name || row.email || "—";
}

export function PoolBurnReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const enterprise = useCompanyEnterpriseStatus();
  const data = enterprise.data;
  const seats = data?.seats ?? [];
  const plan = data?.plan;
  const included = plan?.includedMinutes ?? 0;
  const used = data?.minutesUsed ?? 0;
  const remaining =
    data?.minutesRemaining ??
    (included > 0 ? Math.max(0, included - used) : null);
  const burnPct =
    included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

  return (
    <ReportShell
      title="Seat & pool burn"
      description="Live Enterprise shared pool and seat assignments for your company."
      range={range}
      onRangeChange={setRange}
      showRangeBar={false}
      exportDisabled={!data?.enabled || seats.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "pool-burn",
      }}
      enablePdf
    >
      {enterprise.isLoading ? (
        <GridCell>
          <p className="py-6 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        </GridCell>
      ) : !data?.enabled || !plan ? (
        <>
          <ReportSectionHeader title="No active Enterprise plan" />
          <GridCell>
            <p className="text-sm text-muted-foreground">
              Seat & pool burn applies when Super Admin activates a company
              Enterprise shared pool. Team seats and PAYG usage appear under
              Spend by product instead.
            </p>
          </GridCell>
        </>
      ) : (
        <>
          <GridBand>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={data.enabled ? "success" : "muted"}>
                {plan.status}
              </Badge>
              <span>
                Period {formatDate(plan.periodStart)} →{" "}
                {formatDate(plan.periodEnd)}
              </span>
            </div>
          </GridBand>

          <ReportKpiRow className="xl:grid-cols-2">
            <ReportKpiCell>
              <StatCard
                variant="cell"
                label="Minutes used"
                icon={Gauge}
                value={`${used} / ${included}`}
                hint={
                  remaining == null
                    ? undefined
                    : `${remaining} remaining · ${burnPct}% burned`
                }
              />
            </ReportKpiCell>
            <ReportKpiCell>
              <StatCard
                variant="cell"
                label="Seats used"
                icon={Users}
                value={`${data.seatsUsed} / ${plan.seatLimit}`}
                hint={`${data.seatsRemaining} remaining`}
              />
            </ReportKpiCell>
          </ReportKpiRow>

          <ReportSectionHeader
            title="Pool burn"
            description="Shared Meeting Intel + Notes minutes for the current Enterprise period."
          />
          <GridCell>
            <div className="h-3 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-brand transition-all"
                style={{ width: `${burnPct}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {burnPct}% of included minutes used
            </p>
          </GridCell>

          <ReportSectionHeader
            title="Seated employees"
            description="Only seated users draw from the shared pool."
          />
          <ReportTableCell>
            <ReportTable
              headers={[
                "Employee",
                "Role",
                "Status",
                { label: "Assigned", align: "right" },
              ]}
              empty={seats.length === 0}
              emptyMessage="No seats assigned yet."
            >
              {seats.map((row) => (
                <tr key={row.userId} className="hover:bg-secondary/20">
                  <td className="px-4 py-2">
                    <div className="font-medium">{displayName(row)}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.email}
                    </div>
                  </td>
                  <td className="px-4 py-2 capitalize text-muted-foreground">
                    {row.role || "—"}
                  </td>
                  <td className="px-4 py-2 capitalize">{row.status || "—"}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground">
                    {formatDate(row.assignedAt)}
                  </td>
                </tr>
              ))}
            </ReportTable>
          </ReportTableCell>
        </>
      )}
    </ReportShell>
  );
}
